import {
    CalendarDate,
    formatDateNoTime,
    FranchiseDefaultFeeStructureId,
    FranchiseFeeStructureMode,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { inferProcedureOutput } from "@trpc/server";
import { ContentFull, ContentHeader } from "components/layout";
import { trpc } from "components/rpc/trpc";
import { DeleteButton, EditButton } from "components/ui/action-button";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { PrimaryButton, PrimaryCompactButton } from "components/ui/primary-button";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import { Spinner } from "components/ui/spinner";
import { useMessage } from "hooks/use-message";
import React from "react";
import {
    basisPointsToPercentInput,
    centsToDollarsInput,
    feeAmountColumnLabel,
    feeScheduleLabel,
    feeStructureModeLabel,
    formatDollars,
    modeChangePatch,
    nextFormKey,
    parseBasisPoints,
    parseCents,
    stepWhenLabel,
} from "./fee-schedule-form";

type DefaultFeeStructure = inferProcedureOutput<
    AppRouter["franchiseTerritory"]["listDefaultFeeStructures"]
>[number];

interface DefaultStepFormData {
    _key: string;
    monthsDisplay: string;
    amountDollars: string;
}

interface DefaultFeeStructureDraft {
    name: string;
    mode: FranchiseFeeStructureMode;
    feePercentDisplay: string;
    steps: DefaultStepFormData[];
}

interface ValidStep {
    monthsAfterCommencement: number;
    amountCents: number;
}

function parseSteps(steps: DefaultStepFormData[]): ValidStep[] {
    return steps
        .filter(s => s.monthsDisplay.trim() !== "" && s.amountDollars.trim() !== "")
        .map(s => ({
            monthsAfterCommencement: parseInt(s.monthsDisplay, 10),
            amountCents: parseCents(s.amountDollars),
        }))
        .filter(s => Number.isInteger(s.monthsAfterCommencement))
        .sort((a, b) => a.monthsAfterCommencement - b.monthsAfterCommencement);
}

function emptyDraft(): DefaultFeeStructureDraft {
    return {
        name: "",
        mode: FranchiseFeeStructureMode.PercentPlusMinimum,
        feePercentDisplay: "",
        steps: [
            {
                _key: nextFormKey(),
                monthsDisplay: "0",
                amountDollars: "",
            },
        ],
    };
}

function draftFromStructure(structure: DefaultFeeStructure): DefaultFeeStructureDraft {
    return {
        name: structure.name,
        mode: structure.mode,
        feePercentDisplay: basisPointsToPercentInput(structure.feePercentBasisPoints),
        steps: structure.steps.map(s => ({
            _key: s.id,
            monthsDisplay: String(s.monthsAfterCommencement),
            amountDollars: centsToDollarsInput(s.amountCents),
        })),
    };
}

function draftError(draft: DefaultFeeStructureDraft): string | null {
    if (!draft.name.trim()) {
        return "Name is required.";
    }
    if (
        draft.mode === FranchiseFeeStructureMode.PercentPlusMinimum &&
        (!draft.feePercentDisplay.trim() || parseFloat(draft.feePercentDisplay) <= 0)
    ) {
        return "Fee percent is required and must be greater than 0.";
    }
    const months: number[] = [];
    for (const step of draft.steps) {
        const parsedMonths = parseInt(step.monthsDisplay, 10);
        if (
            step.monthsDisplay.trim() === "" ||
            !Number.isInteger(parsedMonths) ||
            parsedMonths < 0
        ) {
            return "Every scheduled change needs a month offset of zero or more.";
        }
        if (!step.amountDollars.trim() || parseFloat(step.amountDollars) < 0) {
            return "Every schedule row needs an amount.";
        }
        months.push(parsedMonths);
    }
    if (!months.includes(0)) {
        return "The schedule needs an at-commencement row.";
    }
    if (new Set(months).size !== months.length) {
        return "Scheduled changes must have unique month offsets.";
    }
    return null;
}

function buildInput(draft: DefaultFeeStructureDraft) {
    return {
        name: draft.name,
        mode: draft.mode,
        ...(draft.mode === FranchiseFeeStructureMode.PercentPlusMinimum
            ? {
                  feePercentBasisPoints: parseBasisPoints(draft.feePercentDisplay),
              }
            : {}),
        steps: draft.steps.map(s => ({
            monthsAfterCommencement: parseInt(s.monthsDisplay, 10),
            amountCents: parseCents(s.amountDollars),
        })),
    };
}

const TIMELINE_SHADES = [
    "bg-brand-100 text-brand-900",
    "bg-brand-300 text-brand-900",
    "bg-brand-600 text-white",
    "bg-brand-800 text-white",
    "bg-brand-900 text-white",
];

interface TimelineAxisLabel {
    month: number;
    pct: number;
}

function timelineAxisLabels(steps: ValidStep[], horizon: number): TimelineAxisLabel[] {
    const labels: TimelineAxisLabel[] = [];
    let lastPct = 12;
    for (const step of steps.slice(1)) {
        const pct = (step.monthsAfterCommencement / horizon) * 100;
        if (pct - lastPct < 10) {
            continue;
        }
        labels.push({ month: step.monthsAfterCommencement, pct });
        lastPct = pct;
    }
    return labels;
}

const ScheduleTimeline: React.FC<{ steps: ValidStep[] }> = props => {
    const { steps } = props;
    if (steps.length === 0) {
        return null;
    }
    const lastStart = steps[steps.length - 1].monthsAfterCommencement;
    const horizon = Math.max(lastStart + 12, 12);
    return (
        <div className="mt-3">
            <div className="flex w-full h-8 rounded-md overflow-hidden ring-1 ring-brand-300">
                {steps.map((step, i) => {
                    const end =
                        i + 1 < steps.length
                            ? steps[i + 1].monthsAfterCommencement
                            : horizon;
                    const width = ((end - step.monthsAfterCommencement) / horizon) * 100;
                    const shade =
                        TIMELINE_SHADES[Math.min(i, TIMELINE_SHADES.length - 1)];
                    return (
                        <div
                            key={`${step.monthsAfterCommencement}-${i}`}
                            className={`${shade} flex items-center justify-center text-xs font-semibold min-w-0 border-r border-white/40 last:border-r-0`}
                            style={{ width: `${width}%` }}>
                            <span className="truncate px-1">
                                {formatDollars(step.amountCents)}
                                {i === steps.length - 1 ? " →" : ""}
                            </span>
                        </div>
                    );
                })}
            </div>
            <div className="relative h-4 mt-0.5 text-[10px] text-gray-500">
                <span className="absolute left-0">commencement</span>
                {timelineAxisLabels(steps, horizon).map(label => (
                    <span
                        key={label.month}
                        className={`absolute ${
                            label.pct > 92 ? "-translate-x-full" : "-translate-x-1/2"
                        }`}
                        style={{ left: `${label.pct}%` }}>
                        {label.month} mo
                    </span>
                ))}
            </div>
        </div>
    );
};

const LiveExample: React.FC<{
    steps: ValidStep[];
    mode: FranchiseFeeStructureMode;
    feePercentDisplay: string;
}> = props => {
    const { steps, mode, feePercentDisplay } = props;
    if (steps.length === 0) {
        return null;
    }
    const today = CalendarDate.todayNoTime;
    return (
        <div className="mt-3 rounded-md bg-gray-50 border border-gray-200 px-3 py-2.5">
            <p className="text-xs font-semibold text-gray-700 mb-1.5">
                Example — a fee structure commencing {formatDateNoTime(today.toString())}{" "}
                would charge:
            </p>
            <ul className="space-y-0.5">
                {steps.map((step, i) => {
                    const from = today.addMonths(step.monthsAfterCommencement);
                    const next = steps[i + 1];
                    const amount =
                        mode === FranchiseFeeStructureMode.PercentPlusMinimum
                            ? `${feePercentDisplay || "—"}% with a ${formatDollars(step.amountCents)}/mo minimum`
                            : `${formatDollars(step.amountCents)}/mo`;
                    return (
                        <li
                            key={`${step.monthsAfterCommencement}-${i}`}
                            className="text-xs text-gray-600">
                            <span className="font-medium text-gray-800">
                                {formatDateNoTime(from.toString())}
                            </span>{" "}
                            {next ? (
                                <>
                                    –{" "}
                                    <span className="font-medium text-gray-800">
                                        {formatDateNoTime(
                                            today
                                                .addMonths(next.monthsAfterCommencement)
                                                .toString(),
                                        )}
                                    </span>
                                </>
                            ) : (
                                <span className="font-medium text-gray-800">onward</span>
                            )}
                            : {amount}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

interface EditCardProps {
    value: DefaultFeeStructureDraft;
    onChange: (value: DefaultFeeStructureDraft) => void;
    footer: React.ReactNode;
}

const DefaultFeeStructureEditCard: React.FC<EditCardProps> = props => {
    const { value, onChange, footer } = props;
    const validSteps = parseSteps(value.steps);
    const error = draftError(value);

    const updateStep = (
        stepIndex: number,
        field: "monthsDisplay" | "amountDollars",
        fieldValue: string,
    ) => {
        onChange({
            ...value,
            steps: value.steps.map((step, i) =>
                i === stepIndex ? { ...step, [field]: fieldValue } : step,
            ),
        });
    };

    const addStep = () => {
        const maxMonth = Math.max(
            0,
            ...value.steps.map(s => parseInt(s.monthsDisplay, 10) || 0),
        );
        onChange({
            ...value,
            steps: [
                ...value.steps,
                {
                    _key: nextFormKey(),
                    monthsDisplay: String(maxMonth + 12),
                    amountDollars: "",
                },
            ],
        });
    };

    const removeStep = (stepIndex: number) => {
        onChange({
            ...value,
            steps: value.steps.filter((_, i) => i !== stepIndex),
        });
    };

    return (
        <div className="space-y-3">
            <Input
                label="Name"
                textSize="sm"
                value={value.name}
                onChange={e => onChange({ ...value, name: e.target.value })}
            />
            <Select
                label="Mode"
                textSize="sm"
                value={value.mode}
                onChange={e =>
                    onChange({
                        ...value,
                        ...modeChangePatch(
                            value.feePercentDisplay,
                            e.target.value as FranchiseFeeStructureMode,
                        ),
                    })
                }>
                <option value={FranchiseFeeStructureMode.FlatFee}>
                    {feeStructureModeLabel(FranchiseFeeStructureMode.FlatFee)}
                </option>
                <option value={FranchiseFeeStructureMode.PercentPlusMinimum}>
                    {feeStructureModeLabel(FranchiseFeeStructureMode.PercentPlusMinimum)}
                </option>
            </Select>
            {value.mode === FranchiseFeeStructureMode.PercentPlusMinimum ? (
                <Input
                    label="Monthly fee percent (%)"
                    textSize="sm"
                    type="number"
                    step="0.01"
                    min="0"
                    value={value.feePercentDisplay}
                    onChange={e =>
                        onChange({ ...value, feePercentDisplay: e.target.value })
                    }
                />
            ) : null}
            <div>
                <p className="text-sm font-medium text-gray-700">
                    {feeScheduleLabel(value.mode)}
                </p>
                <p className="text-xs text-gray-500 mt-0.5 mb-2">
                    Steps are relative to each fee structure&apos;s commencement date,
                    entered when the territory is created.
                </p>
                <table className="text-sm">
                    <thead>
                        <tr>
                            <th className="text-left pb-1 pr-3 font-medium text-gray-600">
                                When
                            </th>
                            <th className="text-left pb-1 pr-2 font-medium text-gray-600">
                                {feeAmountColumnLabel(value.mode)}
                            </th>
                            <th className="w-8"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {value.steps.map((step, stepIndex) => {
                            const isFirst = stepIndex === 0;
                            return (
                                <tr key={step._key}>
                                    <td className="pr-3 pb-1">
                                        {isFirst ? (
                                            <span className="inline-flex items-center h-8 text-gray-800 font-medium">
                                                At commencement
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1.5">
                                                <span className="text-gray-600">
                                                    After
                                                </span>
                                                <span className="inline-block w-20">
                                                    <Input
                                                        type="number"
                                                        textSize="sm"
                                                        min="1"
                                                        value={step.monthsDisplay}
                                                        onChange={e =>
                                                            updateStep(
                                                                stepIndex,
                                                                "monthsDisplay",
                                                                e.target.value,
                                                            )
                                                        }
                                                    />
                                                </span>
                                                <span className="text-gray-600">
                                                    months
                                                </span>
                                            </span>
                                        )}
                                    </td>
                                    <td className="pr-2 pb-1">
                                        <span className="inline-block w-32">
                                            <Input
                                                type="number"
                                                textSize="sm"
                                                step="0.01"
                                                min="0"
                                                value={step.amountDollars}
                                                onChange={e =>
                                                    updateStep(
                                                        stepIndex,
                                                        "amountDollars",
                                                        e.target.value,
                                                    )
                                                }
                                            />
                                        </span>
                                    </td>
                                    <td className="pb-1">
                                        {!isFirst ? (
                                            <DeleteButton
                                                backgroundColor="bg-gray-100"
                                                size="sm"
                                                onClick={() => removeStep(stepIndex)}
                                            />
                                        ) : null}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <SecondaryCompactButton onClick={addStep}>
                    Add a scheduled change
                </SecondaryCompactButton>
            </div>
            <ScheduleTimeline steps={validSteps} />
            <LiveExample
                steps={validSteps}
                mode={value.mode}
                feePercentDisplay={value.feePercentDisplay}
            />
            {error ? <div className="text-sm text-amber-600">{error}</div> : null}
            {footer}
        </div>
    );
};

interface SavedRowProps {
    structure: DefaultFeeStructure;
    onSave: (id: FranchiseDefaultFeeStructureId, draft: DefaultFeeStructureDraft) => void;
    onDelete: (id: FranchiseDefaultFeeStructureId) => void;
}

const SavedDefaultFeeStructureRow: React.FC<SavedRowProps> = props => {
    const { structure, onSave, onDelete } = props;
    const [editing, setEditing] = React.useState(false);
    const [draft, setDraft] = React.useState<DefaultFeeStructureDraft>(() =>
        draftFromStructure(structure),
    );

    if (editing) {
        return (
            <div className="rounded-md border border-gray-300 bg-gray-50 p-4">
                <DefaultFeeStructureEditCard
                    value={draft}
                    onChange={setDraft}
                    footer={
                        <div className="flex items-center gap-2">
                            <PrimaryCompactButton
                                disabled={draftError(draft) !== null}
                                onClick={() => {
                                    onSave(structure.id, draft);
                                    setEditing(false);
                                }}>
                                Save
                            </PrimaryCompactButton>
                            <SecondaryCompactButton onClick={() => setEditing(false)}>
                                Cancel
                            </SecondaryCompactButton>
                        </div>
                    }
                />
            </div>
        );
    }

    return (
        <div className="rounded-md border border-gray-200 p-4 space-y-2">
            <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                    {structure.name}
                </span>
                <div className="flex items-center space-x-2">
                    <EditButton
                        backgroundColor="bg-white"
                        size="sm"
                        onClick={() => {
                            setDraft(draftFromStructure(structure));
                            setEditing(true);
                        }}
                    />
                    <DeleteButton
                        backgroundColor="bg-white"
                        size="sm"
                        confirm={{
                            title: "Delete Default Fee Structure?",
                            message: `Remove "${structure.name}" from your territory defaults? Existing territories are not affected.`,
                            confirmLabel: "Delete",
                        }}
                        onClick={() => onDelete(structure.id)}
                    />
                </div>
            </div>
            <p className="text-sm text-gray-500">
                {feeStructureModeLabel(structure.mode)}
                {structure.mode === FranchiseFeeStructureMode.PercentPlusMinimum
                    ? ` · ${basisPointsToPercentInput(structure.feePercentBasisPoints)}% monthly fee`
                    : ""}
            </p>
            <ul className="space-y-0.5">
                {structure.steps.map(step => (
                    <li key={step.id} className="text-sm text-gray-600">
                        <span className="font-medium text-gray-800">
                            {stepWhenLabel(step.monthsAfterCommencement)}
                        </span>
                        : {formatDollars(step.amountCents)}/mo
                        {structure.mode === FranchiseFeeStructureMode.PercentPlusMinimum
                            ? " minimum"
                            : ""}
                    </li>
                ))}
            </ul>
            <ScheduleTimeline steps={structure.steps} />
        </div>
    );
};

interface NewRowProps {
    pending: boolean;
    onCreate: (draft: DefaultFeeStructureDraft) => void;
    onCancel: () => void;
}

const NewDefaultFeeStructureRow: React.FC<NewRowProps> = props => {
    const { pending, onCreate, onCancel } = props;
    const [draft, setDraft] = React.useState<DefaultFeeStructureDraft>(emptyDraft);
    return (
        <div className="rounded-md border border-gray-300 bg-gray-50 p-4">
            <DefaultFeeStructureEditCard
                value={draft}
                onChange={setDraft}
                footer={
                    <div className="flex items-center gap-2">
                        <PrimaryCompactButton
                            disabled={pending || draftError(draft) !== null}
                            onClick={() => onCreate(draft)}>
                            Save
                        </PrimaryCompactButton>
                        <SecondaryCompactButton onClick={onCancel}>
                            Cancel
                        </SecondaryCompactButton>
                    </div>
                }
            />
        </div>
    );
};

export const FranchiseSettingsDefaults: React.FC<unknown> = () => {
    const trpcClient = trpc.useTRPC();
    const { data, isLoading, refetch } = useQuery(
        trpcClient.franchiseTerritory.listDefaultFeeStructures.queryOptions(),
    );

    const [newRowKeys, setNewRowKeys] = React.useState<string[]>([]);
    const { message, setMessage } = useMessage();

    const onSuccess = () => {
        refetch();
    };

    const onError = (error: { message?: string }) => {
        setMessage("error", error.message || "Failed to save territory defaults.");
    };

    const createMutation = useMutation({
        ...trpcClient.franchiseTerritory.createDefaultFeeStructure.mutationOptions(),
        onSuccess,
        onError,
    });
    const updateMutation = useMutation({
        ...trpcClient.franchiseTerritory.updateDefaultFeeStructure.mutationOptions(),
        onSuccess,
        onError,
    });
    const deleteMutation = useMutation({
        ...trpcClient.franchiseTerritory.deleteDefaultFeeStructure.mutationOptions(),
        onSuccess,
        onError,
    });

    const addNewRow = () => {
        setNewRowKeys(prev => [...prev, nextFormKey()]);
    };

    const cancelNewRow = (key: string) => {
        setNewRowKeys(prev => prev.filter(k => k !== key));
    };

    const createFromRow = (key: string, draft: DefaultFeeStructureDraft) => {
        createMutation.mutate(buildInput(draft), {
            onSuccess: () => cancelNewRow(key),
        });
    };

    const saveStructure = (
        id: FranchiseDefaultFeeStructureId,
        draft: DefaultFeeStructureDraft,
    ) => {
        updateMutation.mutate({ defaultFeeStructureId: id, ...buildInput(draft) });
    };

    const deleteStructure = (id: FranchiseDefaultFeeStructureId) => {
        deleteMutation.mutate({ defaultFeeStructureId: id });
    };

    const structures = data ?? [];
    const isEmpty = structures.length === 0 && newRowKeys.length === 0;

    return (
        <div className="space-y-4 lg:space-y-8">
            <ContentFull>
                <ContentHeader>New Territory Defaults</ContentHeader>
                {isLoading ? (
                    <Spinner />
                ) : (
                    <div className="space-y-4 max-w-3xl">
                        <InlineBanner type="info">
                            These defaults pre-fill the fee structures on every new
                            territory. Schedules are defined in months after each fee
                            structure&apos;s commencement date — actual dates are set when
                            the territory is created. Changing defaults never affects
                            existing territories.
                        </InlineBanner>
                        {message ? (
                            <InlineBanner type={message.type}>
                                {message.content}
                            </InlineBanner>
                        ) : null}
                        {isEmpty ? (
                            <p className="text-sm text-gray-500">
                                No default fee structures have been created yet.
                            </p>
                        ) : (
                            <div className="space-y-4">
                                {structures.map(structure => (
                                    <SavedDefaultFeeStructureRow
                                        key={structure.id}
                                        structure={structure}
                                        onSave={saveStructure}
                                        onDelete={deleteStructure}
                                    />
                                ))}
                                {newRowKeys.map(key => (
                                    <NewDefaultFeeStructureRow
                                        key={key}
                                        pending={createMutation.isPending}
                                        onCreate={draft => createFromRow(key, draft)}
                                        onCancel={() => cancelNewRow(key)}
                                    />
                                ))}
                            </div>
                        )}
                        <div>
                            <PrimaryButton onClick={addNewRow}>
                                Add default fee structure
                            </PrimaryButton>
                        </div>
                    </div>
                )}
            </ContentFull>
        </div>
    );
};
